import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "../ui/Button";
import { IconDownload, IconQrcode } from "@tabler/icons-react";
import { useActiveUserPartner } from "@/store/selectors";
import { buildShareableLink, getAssignmentLink } from "@/lib/shareableLink";

interface AssignmentQRCodeProps {
  assignmentId: string;
  size?: number;
  includeMargin?: boolean;
  minimal?: boolean;
  disabled?: boolean;
}

const PADDING = 12;
const BORDER = 1;

export default function AssignmentQRCode({
  assignmentId,
  size = 240,
  includeMargin = true,
  minimal = false,
  disabled = false,
}: AssignmentQRCodeProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const userPartner = useActiveUserPartner();
  const url = buildShareableLink(getAssignmentLink(assignmentId), {
    partnerSlug: userPartner?.slug,
  });
  const previewWidth = size + PADDING * 2 + BORDER * 2;

  const downloadPng = () => {
    if (disabled) return;
    const canvas = canvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    const pngUrl = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = pngUrl;
    a.download = "advize-assignment-qr-code.png";
    a.click();
  };

  if (minimal) {
    return (
      <>
        <div ref={canvasRef} style={{ position: "absolute", left: -9999 }}>
          <QRCodeCanvas
            value={url}
            size={size}
            includeMargin={includeMargin}
            style={{
              filter: disabled ? "blur(8px)" : "none",
              opacity: disabled ? 0.2 : 1,
            }}
          />
        </div>
        <Button
          variant="light"
          color="primary"
          leftSection={<IconQrcode size={12} />}
          radius="xl"
          size="xs"
          fz={10}
          h={24}
          py={0}
          disabled={disabled}
          onClick={downloadPng}
        >
          Download QR Code
        </Button>
      </>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        width: previewWidth,
      }}
    >
      <div
        ref={canvasRef}
        style={{
          width: "fit-content",
          padding: PADDING,
        }}
      >
        <QRCodeCanvas
          value={url}
          size={size}
          includeMargin={includeMargin}
          style={{
            filter: disabled ? "blur(6px)" : "none",
            opacity: disabled ? 0.7 : 1,
          }}
        />
      </div>
      <Button
        variant="light"
        fullWidth
        radius="xl"
        onClick={downloadPng}
        leftSection={<IconDownload size={16} />}
        disabled={disabled}
      >
        Download QR Code
      </Button>
    </div>
  );
}
